import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faCheck } from "@fortawesome/free-solid-svg-icons";

/**
 * Pill-shaped dropdown button with a popover menu.
 * Shows the selected value (dark fill) when it differs from defaultValue.
 */
const FilterDropdown = ({
  id,
  label,
  value,
  options,
  onChange,
  defaultValue,
  align = "left",
  showValueInLabel = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const isActive = defaultValue !== undefined && value !== defaultValue;
  const buttonText = showValueInLabel
    ? `${label}: ${selected?.label ?? ""}`
    : isActive
      ? selected?.label
      : label;

  return (
    <div className={`shop-dd ${open ? "is-open" : ""}`} ref={ref}>
      <button
        id={id}
        type="button"
        className={`shop-dd-trigger ${isActive ? "is-active" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{buttonText}</span>
        <FontAwesomeIcon icon={faChevronDown} className="shop-dd-chevron" />
      </button>

      <ul className={`shop-dd-menu shop-dd-menu--${align}`} role="listbox" aria-labelledby={id}>
        {options.map((opt) => {
          const isSelected = opt.value === value;
          return (
            <li key={opt.value} role="option" aria-selected={isSelected}>
              <button
                type="button"
                className={`shop-dd-option ${isSelected ? "is-selected" : ""}`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                <span>{opt.label}</span>
                {isSelected && <FontAwesomeIcon icon={faCheck} />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default FilterDropdown;
